import { useRef, useState } from 'react';
import { Loader2, Music, X } from 'lucide-react';
import { uploadFile } from '../../api/endpoints';
import { assetUrl } from '../../api/client';

interface AudioUploaderProps {
  value: string | null;
  onChange: (url: string | null) => void;
}

/** 音频上传器（背景音乐等），MP3/WAV/OGG/M4A/FLAC，不超过 25MB */
export default function AudioUploader({ value, onChange }: AudioUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    setUploading(true);
    try {
      const result = await uploadFile(file);
      onChange(result.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : '上传失败');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="image-uploader">
      {value ? (
        <audio className="audio-uploader-preview" controls preload="none" src={assetUrl(value) ?? ''} />
      ) : (
        <span className="image-uploader-placeholder" aria-hidden="true">
          {uploading ? <Loader2 className="spinner-sm" /> : <Music size={20} />}
        </span>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? '上传中…' : value ? '更换音乐' : '上传音乐'}
          </button>
          {value && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => onChange(null)}
              aria-label="移除音乐"
            >
              <X size={15} />
            </button>
          )}
        </div>
        <span className="form-hint">支持 MP3、WAV、OGG、M4A、FLAC，大小不超过 25MB</span>
        {error && <span className="form-error">{error}</span>}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="audio/mpeg,audio/mp3,audio/wav,audio/x-wav,audio/ogg,audio/mp4,audio/x-m4a,audio/flac"
        style={{ display: 'none' }}
        onChange={(event) => void handleFile(event.target.files?.[0])}
      />
    </div>
  );
}
